import React from 'react';
import { Thermometer, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';

/**
 * Temperature thresholds for T50 (sensor_4) - LPT Outlet Temperature
 * This is a proxy for EGT (Exhaust Gas Temperature) which isn't directly in dataset
 * Values in degrees Rankine (°R)
 */
const T50_THRESHOLDS = {
  normal_max: 1420,   // Below this is normal
  warning: 1420,      // Warning zone starts
  critical: 1450,     // Critical zone starts
  redline: 1500,      // Do not exceed
  gauge_min: 1350,    // Gauge display minimum
  gauge_max: 1520,    // Gauge display maximum
};

/**
 * TemperatureMarginIndicator - Shows T50/EGT proxy with margin to redline
 * Critical for turbine protection monitoring
 */
export function TemperatureMarginIndicator({ sensorFeatures, compact = false }) {
  // Get sensor_4 (T50) data
  const t50Data = sensorFeatures?.sensor_4;

  if (!t50Data) return null;

  const currentTemp = t50Data.mean || 0;
  const slope = t50Data.slope || 0;
  const margin = T50_THRESHOLDS.redline - currentTemp;

  // Determine zone
  let zone = 'normal';
  if (currentTemp >= T50_THRESHOLDS.critical) {
    zone = 'critical';
  } else if (currentTemp >= T50_THRESHOLDS.warning) {
    zone = 'warning';
  }

  // Calculate gauge position (0-100%)
  const range = T50_THRESHOLDS.gauge_max - T50_THRESHOLDS.gauge_min;
  const position = Math.max(0, Math.min(100,
    ((currentTemp - T50_THRESHOLDS.gauge_min) / range) * 100
  ));

  // Calculate threshold positions for gauge
  const warningPos = ((T50_THRESHOLDS.warning - T50_THRESHOLDS.gauge_min) / range) * 100;
  const criticalPos = ((T50_THRESHOLDS.critical - T50_THRESHOLDS.gauge_min) / range) * 100;
  const redlinePos = ((T50_THRESHOLDS.redline - T50_THRESHOLDS.gauge_min) / range) * 100;

  // Trend indicator
  const isRising = slope > 0.001;
  const isFalling = slope < -0.001;

  if (compact) {
    return (
      <div className={`temp-margin-compact ${zone}`}>
        <Thermometer size={14} />
        <span className="temp-value">{currentTemp.toFixed(0)}°R</span>
        <span className="temp-margin">
          {margin > 0 ? `${margin.toFixed(0)}° to redline` : 'OVER REDLINE'}
        </span>
        {isRising && <TrendingUp size={12} className="temp-trend rising" />}
        {isFalling && <TrendingDown size={12} className="temp-trend falling" />}
      </div>
    );
  }

  return (
    <div className={`temp-margin-indicator ${zone}`}>
      <div className="temp-margin-header">
        <Thermometer size={16} />
        <span className="temp-margin-title">T50 / EGT Margin</span>
        {zone === 'critical' && <AlertTriangle size={14} className="warning-icon" />}
      </div>

      {/* Temperature Gauge Bar */}
      <div className="temp-gauge">
        {/* Zone backgrounds */}
        <div className="temp-zone normal" style={{ width: `${warningPos}%` }} />
        <div className="temp-zone warning" style={{ left: `${warningPos}%`, width: `${criticalPos - warningPos}%` }} />
        <div className="temp-zone critical" style={{ left: `${criticalPos}%`, width: `${redlinePos - criticalPos}%` }} />
        <div className="temp-zone redline" style={{ left: `${redlinePos}%`, width: `${100 - redlinePos}%` }} />

        {/* Current temperature indicator */}
        <div
          className={`temp-indicator ${zone}`}
          style={{ left: `${position}%` }}
        >
          <div className="temp-indicator-line" />
          <div className="temp-indicator-value">{currentTemp.toFixed(0)}</div>
        </div>

        {/* Redline marker */}
        <div className="temp-redline-marker" style={{ left: `${redlinePos}%` }}>
          <div className="redline-line" />
          <span className="redline-label">REDLINE</span>
        </div>
      </div>

      {/* Scale labels */}
      <div className="temp-scale">
        <span>{T50_THRESHOLDS.gauge_min}°R</span>
        <span>{T50_THRESHOLDS.redline}°R</span>
      </div>

      {/* Margin Display */}
      <div className="temp-margin-display">
        <div className="margin-value">
          {margin > 0 ? (
            <>
              <span className={`margin-number ${zone}`}>{margin.toFixed(0)}°R</span>
              <span className="margin-label">margin to redline</span>
            </>
          ) : (
            <>
              <span className="margin-number exceeded">{Math.abs(margin).toFixed(0)}°R</span>
              <span className="margin-label exceeded">OVER REDLINE</span>
            </>
          )}
        </div>

        {/* Trend */}
        <div className="temp-trend-display">
          {isRising && (
            <div className="trend rising">
              <TrendingUp size={14} />
              <span>+{(slope * 100).toFixed(2)}°R/cyc</span>
            </div>
          )}
          {isFalling && (
            <div className="trend falling">
              <TrendingDown size={14} />
              <span>{(slope * 100).toFixed(2)}°R/cyc</span>
            </div>
          )}
          {!isRising && !isFalling && (
            <div className="trend stable">
              <span>Stable</span>
            </div>
          )}
        </div>
      </div>

      {/* Warning message for critical zone */}
      {zone === 'critical' && (
        <div className="temp-warning-message">
          Consider derate to reduce thermal stress
        </div>
      )}
    </div>
  );
}

/**
 * EGTAlertBadge - Compact badge for showing EGT status in alerts
 */
export function EGTAlertBadge({ sensorFeatures }) {
  const t50Data = sensorFeatures?.sensor_4;
  if (!t50Data) return null;

  const currentTemp = t50Data.mean || 0;
  const margin = T50_THRESHOLDS.redline - currentTemp;

  let zone = 'normal';
  if (currentTemp >= T50_THRESHOLDS.critical) {
    zone = 'critical';
  } else if (currentTemp >= T50_THRESHOLDS.warning) {
    zone = 'warning';
  }

  // Only show badge if in warning or critical zone
  if (zone === 'normal') return null;

  return (
    <div className={`egt-alert-badge ${zone}`}>
      <Thermometer size={10} />
      <span>{margin.toFixed(0)}° margin</span>
    </div>
  );
}
